/**
 * Pipeline Builder Store (Zustand)
 *
 * Step-list model: trigger (data source) → transform steps.
 * Output is always the Solidafy catalog — determined by environment.
 */

import yaml from 'js-yaml';
import { create } from 'zustand';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { edenError } from '@solidafy/api-client';
import { useAuthStore } from '@/stores/auth';
import {
  generatePipelineJson,
  composePreviewDsl,
  composeChartPreviewDsl,
  appendOutputTerminal,
} from '@/lib/pipeline-utils';
import { createDashboard, updateDashboard } from '@/lib/bi/dashboard-api';
import type {
  PipelineTrigger,
  TransformStep,
  PipelineChart,
  AlertStep,
  AgentStep,
  IngestStep,
  PipelineNode,
  SourceNodeData,
  MaterializeNodeData,
  PipelineTemplate,
} from '@/types/pipeline';
import type { DataSource, ConnectorListItem, PipelineRun } from '@solidafy/api-client';
import type { DashboardSpec, ChartDefinition, FilterDefinition, FilterType } from '@solidafy/bi-core';
import type { SeedTableDef, SeedColumnDef } from '@/types/seed';

// ============================================================================
// Helpers
// ============================================================================

/** Convert snake_case / kebab-case names to Title Case (e.g. "azure_billing" → "Azure Billing") */
function humanizeTitle(name: string): string {
  return name
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

// ============================================================================
// Pipeline filter (from registry templates) — SOL-82
// ============================================================================

/**
 * Filter definition as declared in pipeline YAML templates (registry).
 *
 * When a template is applied via `applyTemplate()`, its `filters` array is
 * stored here. On `pushChartsToDashboard()` these are converted to dashboard
 * `FilterDefinition` objects so the BI layer renders them automatically.
 *
 * - `scope: 'global'` → time-range override (static options, applied via `.since()`)
 * - `scope: 'auto'`   → dimension filter (dynamic options via `options_dsl`, injected as `.filter()`)
 */
interface PipelineFilter {
  type: string; // 'select', 'multi_select', etc.
  label: string;
  scope: 'global' | 'auto';
  default?: string;
  column?: string;
  operator?: string;
  options?: { label: string; value: string }[];
  options_dsl?: string;
}

// ============================================================================
// State shape
// ============================================================================

interface PipelineBuilderState {
  // Pipeline metadata
  pipelineId: string | null;
  pipelineName: string;
  pipelineDescription: string;
  pipelineStatus: 'draft' | 'active' | 'paused' | 'error';
  pipelineSchedule: string | null;
  /** Registry template this pipeline was created/updated from */
  templateName: string | null;

  // Step-list model
  trigger: PipelineTrigger;
  ingestSteps: IngestStep[];
  transformSteps: TransformStep[];
  charts: PipelineChart[];
  alerts: AlertStep[];
  agentSteps: AgentStep[];
  /** SOL-82: pipeline-level filters seeded from registry templates */
  filters: PipelineFilter[];
  selectedStepId: string | null;
  drilledStepId: string | null;

  // Data
  dataSources: DataSource[];
  loadingDataSources: boolean;

  // UI
  isDirty: boolean;
  isSaving: boolean;
  isRunning: boolean;
  runError: string | null;
  isFullyLoaded: boolean;

  // Preview
  previewStepId: string | null;
  previewLoading: boolean;
  previewData: {
    columns: { name: string; type: string }[];
    rows: Record<string, unknown>[];
    rowCount: number;
    error?: string;
    durationMs?: number;
    composedDsl?: string;
  } | null;
  showPreviewModal: boolean;

  // Chart preview
  chartPreviewId: string | null;
  showChartPreview: boolean;

  // Step execution
  executingStepId: string | null;
  executionJobId: string | null;

  // Dashboard push
  dashboardId: string | null;
  isPushingDashboard: boolean;

  // Seed tables (read-only, from pipeline_json)
  seedTables: SeedTableDef[];

  // Registry
  connectors: ConnectorListItem[];
  loadingConnectors: boolean;
  pipelineTemplates: PipelineTemplate[];

  // Runs
  runs: PipelineRun[];

  // Actions — data loading
  loadPipeline: (id: string) => Promise<boolean>;
  savePipeline: () => Promise<boolean>;
  runPipeline: () => Promise<PipelineRun | null>;
  loadConnectors: () => Promise<void>;
  loadPipelineTemplates: () => Promise<void>;
  loadRuns: () => Promise<void>;
  loadDataSources: () => Promise<void>;

  // Actions — trigger
  setTrigger: (dataSourceId: string) => void;
  clearTrigger: () => void;
  unlinkTrigger: () => void;
  addStream: (name: string) => void;
  removeStream: (name: string) => void;

  // Actions — ingest steps
  addIngestStep: () => string;
  removeIngestStep: (id: string) => void;
  updateIngestStep: (id: string, partial: Partial<IngestStep>) => void;
  /** Update an ingest step without marking the pipeline dirty (for auto-linking, not user edits) */
  updateIngestStepSilent: (id: string, partial: Partial<IngestStep>) => void;

  // Actions — steps
  addTransformStep: () => string;
  removeTransformStep: (id: string) => void;
  updateTransformStep: (id: string, partial: Partial<TransformStep>) => void;
  reorderSteps: (ids: string[]) => void;

  // Actions — selection / drill-in
  selectStep: (id: string | null) => void;
  drillIntoStep: (id: string) => void;
  exitDrillIn: () => void;

  // Actions — preview
  /**
   * Run a DSL preview for stepId.
   * Pass inModal=true when called from within DslEditorModal so the global
   * overlay in PipelineBuilderPage is suppressed (the modal has its own pane).
   */
  previewStep: (stepId: string, inModal?: boolean) => Promise<void>;
  getComposedDsl: (stepId: string) => string;
  closePreview: () => void;

  // Actions — execute step
  executeStep: (stepId: string) => Promise<{ jobId: string; jobName: string } | null>;

  // Actions — charts
  addChart: () => string;
  updateChart: (id: string, partial: Partial<PipelineChart>) => void;
  removeChart: (id: string) => void;
  previewChart: (id: string) => void;
  closeChartPreview: () => void;
  getChartComposedDsl: (chartId: string) => string;

  // Actions — alerts
  addAlert: () => string;
  updateAlert: (id: string, partial: Partial<AlertStep>) => void;
  removeAlert: (id: string) => void;

  // Actions — agent steps
  addAgentStep: () => string;
  updateAgentStep: (id: string, partial: Partial<AgentStep>) => void;
  removeAgentStep: (id: string) => void;

  // Actions — dashboard
  pushChartsToDashboard: () => Promise<string | null>;

  // Actions — template
  applyTemplate: (templateName: string) => Promise<void>;
  /** Re-apply the stored template and update the dashboard */
  refreshTemplate: () => Promise<boolean>;

  // Actions — AI import
  importFromYaml: (yamlStr: string) => Promise<{ pipelineId: string; dashboardId: string | null }>;
  importFromDsl: (dsl: string, name?: string) => Promise<{ pipelineId: string; dashboardId: string | null }>;

  // Actions — reset
  resetBuilder: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

function generateId(): string {
  return `step_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

const defaultTrigger: PipelineTrigger = { type: 'none' };

/** Parse seed table definitions from pipeline_json.seed array */
function parseSeedTables(pJson: Record<string, unknown> | undefined | null): SeedTableDef[] {
  if (!pJson || !Array.isArray(pJson.seed)) {
    return [];
  }
  return (pJson.seed as Record<string, unknown>[])
    .map((s) => ({
      name: (s.name as string) || '',
      output: (s.output as string) || '',
      mode: (s.mode as 'merge' | 'replace') || 'merge',
      primary_key: (s.primary_key as string[]) || (s.keys as string[]) || [],
      columns: Array.isArray(s.columns)
        ? (s.columns as Record<string, unknown>[]).map((c) => ({
            name: (c.name as string) || '',
            type: c.type as string | undefined,
            seeded: c.seeded as boolean | undefined,
            editable: c.editable as boolean | undefined,
            lookup: c.lookup as SeedColumnDef['lookup'],
            description: c.description as string | undefined,
          }))
        : [],
      dsl: s.dsl as string | undefined,
      default_rows: s.default_rows as Record<string, unknown>[] | undefined,
      depends_on: s.depends_on as string[] | undefined,
      description: s.description as string | undefined,
    }))
    .filter((t) => t.name);
}

/**
 * Migrate v1 canvas_json (has `nodes` array) → v2 step-list format.
 */
function migrateV1(canvas: Record<string, unknown>): {
  trigger: PipelineTrigger;
  transformSteps: TransformStep[];
} {
  const nodes = (canvas.nodes || []) as PipelineNode[];
  const trigger: PipelineTrigger = { ...defaultTrigger };
  const transformSteps: TransformStep[] = [];

  // Source → trigger
  const sourceNode = nodes.find((n) => n.type === 'source');
  if (sourceNode) {
    const sd = sourceNode.data as SourceNodeData;
    trigger.type = 'datasource';
    trigger.connectorType = sd.connectorName || sd.connectorTitle;
    trigger.dataSourceName = sd.connectorTitle || sd.connectorName;
  }

  // Materialize/transform nodes → steps
  const matNodes = nodes.filter((n) => n.type === 'materialize' || n.type === 'transform');
  matNodes.forEach((node, idx) => {
    const md = node.data as MaterializeNodeData;
    const rawOutput = md.outputTable || '';
    const dotIdx = rawOutput.indexOf('.');
    transformSteps.push({
      id: node.id || generateId(),
      name: md.name || `Step ${idx + 1}`,
      transformNodes: md.transformNodes || [],
      transformEdges: md.transformEdges || [],
      dsl: md.dsl || '',
      outputNamespace: dotIdx > 0 ? rawOutput.slice(0, dotIdx) : md.layer || 'silver',
      outputTable: dotIdx > 0 ? rawOutput.slice(dotIdx + 1) : rawOutput,
      mode: md.mode || 'replace',
      conflictKeys: md.conflictKeys || [],
      partitionBy: md.partitionBy || [],
      dependsOn: md.dependsOn || [],
      order: idx,
    });
  });

  return { trigger, transformSteps };
}

// ============================================================================
// Store
// ============================================================================

export const usePipelineBuilderStore = create<PipelineBuilderState>((set, get) => ({
  pipelineId: null,
  pipelineName: 'Untitled Pipeline',
  pipelineDescription: '',
  pipelineStatus: 'draft',
  pipelineSchedule: null,
  templateName: null,

  trigger: { ...defaultTrigger },
  ingestSteps: [],
  transformSteps: [],
  charts: [],
  alerts: [],
  agentSteps: [],
  filters: [],
  selectedStepId: null,
  drilledStepId: null,

  dataSources: [],
  loadingDataSources: false,

  isDirty: false,
  isSaving: false,
  isRunning: false,
  runError: null,
  isFullyLoaded: false,

  previewStepId: null,
  previewLoading: false,
  previewData: null,
  showPreviewModal: false,

  chartPreviewId: null,
  showChartPreview: false,

  executingStepId: null,
  executionJobId: null,

  dashboardId: null,
  isPushingDashboard: false,

  seedTables: [],

  connectors: [],
  loadingConnectors: false,
  pipelineTemplates: [],

  runs: [],

  // ── Load pipeline ──
  loadPipeline: async (id: string) => {
    try {
      const { data: p, error } = await api.api.v1['visual-pipelines']({ id }).get();
      if (error || !p) {
        throw new Error('Failed to load pipeline');
      }
      const canvas = p.canvas_json || {};

      // Parse full seed table definitions from pipeline_json
      const pJson = (p as { pipeline_json?: Record<string, unknown> }).pipeline_json;
      const seedTables = parseSeedTables(pJson);

      // Detect v2 format (has `version: 2`) vs v1 (has `nodes`)
      if (canvas.version === 2) {
        const v2 = canvas as {
          version: number;
          trigger: PipelineTrigger;
          ingestSteps?: IngestStep[];
          transformSteps: TransformStep[];
          charts?: PipelineChart[];
          alerts?: AlertStep[];
          agentSteps?: AgentStep[];
          seedTables?: SeedTableDef[];
          filters?: PipelineFilter[];
          dashboardId?: string | null;
          templateName?: string | null;
          /** Product-generated pipelines use this field name for the template reference */
          templatePipelineName?: string | null;
          // Legacy field — decompose into ingestSteps if present and ingestSteps is empty
          v2Steps?: Record<string, unknown>[] | null;
        };

        // If canvas has legacy v2Steps but no ingestSteps, decompose connector steps
        let ingestSteps = v2.ingestSteps || [];
        if (ingestSteps.length === 0 && v2.v2Steps && v2.v2Steps.length > 0) {
          ingestSteps = v2.v2Steps
            .filter((step) => step.type === 'connector')
            .map((step, idx) => {
              const dsl = (step.dsl as string) || '';
              const connectorMatch = dsl.match(/connector\("([^"]+)"/);
              const streamMatch = dsl.match(/\.stream\("([^"]+)"/);
              return {
                id: generateId(),
                name: (step.name as string) || '',
                dsl,
                connectorType: connectorMatch?.[1] || '',
                streamName: streamMatch?.[1] || '',
                order: idx,
                dependsOn: Array.isArray(step.depends_on) ? (step.depends_on as string[]) : [],
              };
            });
        }

        set({
          pipelineId: p.id,
          pipelineName: p.name,
          pipelineDescription: p.description ?? '',
          pipelineStatus: p.status,
          pipelineSchedule: p.schedule ?? null,
          templateName: v2.templateName || v2.templatePipelineName || null,
          trigger: v2.trigger || { ...defaultTrigger },
          ingestSteps,
          transformSteps: v2.transformSteps || [],
          charts: v2.charts || [],
          alerts: v2.alerts || [],
          agentSteps: v2.agentSteps || [],
          filters: v2.filters || [],
          dashboardId: v2.dashboardId || null,
          seedTables: v2.seedTables || [],
          isDirty: false,
          isFullyLoaded: true,
        });
      } else {
        // v1 migration — load the data then silently save in v2 format so the
        // user never sees a false "Unsaved changes" prompt just from opening an old pipeline.
        const migrated = migrateV1(canvas);
        set({
          pipelineId: p.id,
          pipelineName: p.name,
          pipelineDescription: p.description ?? '',
          pipelineStatus: p.status,
          pipelineSchedule: p.schedule ?? null,
          trigger: migrated.trigger,
          transformSteps: migrated.transformSteps,
          seedTables,
          isDirty: false,
          isFullyLoaded: true,
        });
        // Fire-and-forget: migrate to v2 in the background. Failure is non-fatal —
        // the user will just see "Unsaved changes" the next time they edit something.
        void get().savePipeline();
      }
      return true;
    } catch {
      toast.error('Failed to load pipeline');
      set({ isFullyLoaded: true });
      return false;
    }
  },

  // ── Save pipeline ──
  savePipeline: async () => {
    const s = get();
    set({ isSaving: true });
    try {
      const pipelineJson = generatePipelineJson(
        s.trigger,
        s.transformSteps,
        s.dataSources,
        s.charts,
        s.alerts,
        s.seedTables,
        s.ingestSteps,
        s.agentSteps,
      );

      const canvasJson = {
        version: 2,
        trigger: s.trigger,
        ingestSteps: s.ingestSteps,
        transformSteps: s.transformSteps,
        charts: s.charts,
        alerts: s.alerts,
        agentSteps: s.agentSteps,
        seedTables: s.seedTables,
        filters: s.filters,
        dashboardId: s.dashboardId,
        templateName: s.templateName,
      };

      if (s.pipelineId) {
        const { error } = await api.api.v1['visual-pipelines']({ id: s.pipelineId }).put({
          name: s.pipelineName,
          description: s.pipelineDescription,
          canvas_json: canvasJson,
          pipeline_json: pipelineJson ?? undefined,
          status: s.pipelineStatus,
          schedule: s.pipelineSchedule,
        });
        if (error) {
          throw new Error(edenError(error, 'Failed to save pipeline'));
        }
      } else {
        const { data, error } = await api.api.v1['visual-pipelines'].post({
          name: s.pipelineName,
          description: s.pipelineDescription,
          canvas_json: canvasJson,
          pipeline_json: pipelineJson ?? undefined,
        });
        if (error || !data || !('id' in data)) {
          throw new Error(error ? edenError(error, 'Failed to create pipeline') : 'Failed to create pipeline');
        }
        set({ pipelineId: data.id });
        // Refresh auth so the newly granted policy for this pipeline is in the store
        await useAuthStore.getState().getCurrentUser();
      }
      set({ isDirty: false, isSaving: false });
      return true;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save pipeline');
      set({ isSaving: false });
      return false;
    }
  },

  // ── Run pipeline ──
  runPipeline: async () => {
    const s = get();
    set({ runError: null });
    if (!s.pipelineId || s.isDirty) {
      const saved = await get().savePipeline();
      if (!saved) {
        set({ runError: 'Failed to save pipeline before running' });
        return null;
      }
    }
    set({ isRunning: true });
    const { data, error } = await api.api.v1['visual-pipelines']({ id: get().pipelineId! }).run.post({});
    if (error || !data || !('id' in data)) {
      const message = error ? edenError(error, 'Unknown error') : 'Unknown error';
      set({ runError: `Failed to run pipeline: ${message}`, isRunning: false });
      return null;
    }
    const run = data as PipelineRun;
    set((state) => ({ runs: [run, ...state.runs], isRunning: false }));
    return run;
  },

  // ── Load connectors ──
  loadConnectors: async () => {
    if (get().connectors.length > 0) {
      return;
    }
    set({ loadingConnectors: true });
    try {
      const { data, error } = await api.api.v1.registry.connectors.get();
      if (!error && data) {
        set({ connectors: data.connectors || [], loadingConnectors: false });
      } else {
        set({ loadingConnectors: false });
      }
    } catch {
      set({ loadingConnectors: false });
    }
  },

  // ── Load templates ──
  loadPipelineTemplates: async () => {
    if (get().pipelineTemplates.length > 0) {
      return;
    }
    try {
      const { data, error } = await api.api.v1.registry.pipelines.get();
      if (!error && data) {
        set({
          pipelineTemplates: (data.templates || []).map((t) => ({
            name: t.name,
            title: t.title,
            description: t.description ?? undefined,
            connector: t.connector ?? '',
            streams: t.streams ?? [],
            outputFormat: t.output_format ?? '',
            materializations: t.materializations ?? undefined,
          })),
        });
      }
    } catch {
      // ignore
    }
  },

  // ── Load runs ──
  loadRuns: async () => {
    const id = get().pipelineId;
    if (!id) {
      return;
    }
    const { data } = await api.api.v1['visual-pipelines']({ id }).runs.get();
    if (data) {
      set({ runs: data.runs });
    }
  },

  // ── Load data sources (scoped to active environment) ──
  loadDataSources: async () => {
    set({ loadingDataSources: true });
    const envId = useAuthStore.getState().user?.active_environment_id;
    const { data, error } = await api.api.v1['data-sources'].get({
      query: { environment_id: envId ?? undefined },
    });
    if (error || !data) {
      set({ loadingDataSources: false });
    } else {
      set({ dataSources: data, loadingDataSources: false });
    }
  },

  // ── Trigger ──
  setTrigger: (dataSourceId: string) => {
    const ds = get().dataSources.find((d) => d.id === dataSourceId);
    if (!ds) {
      return;
    }
    // Copy streams from data source into trigger
    const streams: Record<string, string | null> = {};
    if (ds.streams) {
      for (const [name, cfg] of Object.entries(ds.streams)) {
        streams[name] = cfg.sync_mode === 'incremental' ? cfg.cursor_field || null : null;
      }
    }
    // Preserve any additional streams already on the trigger (e.g. from template)
    const existing = get().trigger.streams || {};
    for (const [name, cursor] of Object.entries(existing)) {
      if (!(name in streams)) {
        streams[name] = cursor;
      }
    }
    set({
      trigger: {
        type: 'datasource',
        dataSourceId: ds.id,
        dataSourceName: ds.name,
        connectorType: ds.connector_type,
        streams,
      },
      isDirty: true,
    });
  },

  clearTrigger: () =>
    set({
      trigger: { ...defaultTrigger },
      isDirty: true,
    }),

  unlinkTrigger: () =>
    set((state) => ({
      trigger: {
        ...state.trigger,
        dataSourceId: undefined,
        dataSourceName: undefined,
      },
      isDirty: true,
    })),

  addStream: (name: string) =>
    set((state) => {
      const streams = { ...(state.trigger.streams || {}), [name]: null };
      return { trigger: { ...state.trigger, streams }, isDirty: true };
    }),

  removeStream: (name: string) =>
    set((state) => {
      const streams = { ...(state.trigger.streams || {}) };
      delete streams[name];
      return { trigger: { ...state.trigger, streams }, isDirty: true };
    }),

  // ── Transform steps ──
  // ── Ingest steps ──
  addIngestStep: () => {
    const id = generateId();
    set((state) => ({
      ingestSteps: [
        ...state.ingestSteps,
        {
          id,
          name: '',
          dsl: '',
          connectorType: state.trigger.connectorType || '',
          streamName: '',
          order: state.ingestSteps.length,
          dependsOn: [],
        },
      ],
      selectedStepId: `ingest_${id}`,
      isDirty: true,
    }));
    return id;
  },

  removeIngestStep: (id: string) =>
    set((state) => ({
      ingestSteps: state.ingestSteps.filter((s) => s.id !== id),
      selectedStepId: state.selectedStepId === `ingest_${id}` ? null : state.selectedStepId,
      isDirty: true,
    })),

  updateIngestStep: (id: string, partial: Partial<IngestStep>) =>
    set((state) => ({
      ingestSteps: state.ingestSteps.map((s) => (s.id === id ? { ...s, ...partial } : s)),
      isDirty: true,
    })),

  updateIngestStepSilent: (id: string, partial: Partial<IngestStep>) =>
    set((state) => ({
      ingestSteps: state.ingestSteps.map((s) => (s.id === id ? { ...s, ...partial } : s)),
    })),

  addTransformStep: () => {
    const id = generateId();
    set((state) => {
      const order = state.transformSteps.length;
      return {
        transformSteps: [
          ...state.transformSteps,
          {
            id,
            name: '',
            transformNodes: [],
            transformEdges: [],
            dsl: '',
            outputNamespace: 'silver',
            outputTable: '',
            mode: 'replace' as const,
            conflictKeys: [],
            dependsOn: [],
            order,
          },
        ],
        selectedStepId: id,
        isDirty: true,
      };
    });
    return id;
  },

  removeTransformStep: (id: string) =>
    set((state) => {
      const filtered = state.transformSteps
        .filter((s) => s.id !== id)
        .map((s, idx) => ({
          ...s,
          order: idx,
          dependsOn: s.dependsOn.filter((dep) => {
            const removed = state.transformSteps.find((ts) => ts.id === id);
            return dep !== removed?.name;
          }),
        }));
      return {
        transformSteps: filtered,
        selectedStepId: state.selectedStepId === id ? null : state.selectedStepId,
        isDirty: true,
      };
    }),

  updateTransformStep: (id: string, partial: Partial<TransformStep>) =>
    set((state) => ({
      transformSteps: state.transformSteps.map((s) => (s.id === id ? { ...s, ...partial } : s)),
      isDirty: true,
    })),

  reorderSteps: (ids: string[]) =>
    set((state) => {
      const byId = new Map(state.transformSteps.map((s) => [s.id, s]));
      const reordered = ids
        .map((id) => byId.get(id))
        .filter((s): s is TransformStep => !!s)
        .map((s, idx) => ({ ...s, order: idx }));
      return { transformSteps: reordered, isDirty: true };
    }),

  // ── Selection / drill-in ──
  selectStep: (id) => set({ selectedStepId: id }),
  drillIntoStep: (id) => set({ drilledStepId: id }),
  exitDrillIn: () => set({ drilledStepId: null }),

  // ── Preview step ──
  previewStep: async (stepId: string, inModal?: boolean) => {
    const step = get().transformSteps.find((s) => s.id === stepId);
    if (!step) {
      return;
    }

    // Resolve namespace from trigger's data source
    const { trigger, dataSources, transformSteps } = get();
    const ds = trigger.dataSourceId ? dataSources.find((d) => d.id === trigger.dataSourceId) : null;
    const namespace = ds?.namespace;

    // Compose DSL with dependency chain + namespace substitution
    const dsl = composePreviewDsl(step, transformSteps, namespace);
    if (!dsl || dsl.startsWith('//')) {
      set({
        previewStepId: stepId,
        previewLoading: false,
        showPreviewModal: !inModal,
        previewData: {
          columns: [],
          rows: [],
          rowCount: 0,
          error: 'No valid DSL to preview. Design a transform first.',
          composedDsl: dsl,
        },
      });
      return;
    }

    set({ previewStepId: stepId, previewLoading: true, previewData: null, showPreviewModal: !inModal });

    const { data, error } = await api.api.v1.transforms.preview.post({ dsl, limit: 10 });
    if (error || !data || !('columns' in data)) {
      const message = error ? edenError(error, 'Preview failed') : 'Preview failed';
      set({
        previewData: {
          columns: [],
          rows: [],
          rowCount: 0,
          error: message,
          composedDsl: dsl,
        },
        previewLoading: false,
      });
    } else {
      set({
        previewData: {
          columns: data.columns || [],
          rows: (data.rows || []) as Record<string, unknown>[],
          rowCount: data.row_count || 0,
          error: data.error ?? undefined,
          durationMs: data.duration_ms ?? undefined,
          composedDsl: dsl,
        },
        previewLoading: false,
      });
    }
  },

  getComposedDsl: (stepId: string) => {
    const step = get().transformSteps.find((s) => s.id === stepId);
    if (!step) {
      return '';
    }
    const { trigger, dataSources, transformSteps } = get();
    const ds = trigger.dataSourceId ? dataSources.find((d) => d.id === trigger.dataSourceId) : null;
    return composePreviewDsl(step, transformSteps, ds?.namespace);
  },

  closePreview: () => set({ showPreviewModal: false, previewStepId: null, previewData: null }),

  // ── Execute step (materialize) ──
  executeStep: async (stepId: string) => {
    const { transformSteps, trigger, dataSources, pipelineId } = get();
    const step = transformSteps.find((s) => s.id === stepId);
    if (!step || !pipelineId) {
      return null;
    }

    // Resolve namespace
    const ds = trigger.dataSourceId ? dataSources.find((d) => d.id === trigger.dataSourceId) : null;
    const namespace = ds?.namespace;

    // Compose DSL with dependency chain
    const composedDsl = composePreviewDsl(step, transformSteps, namespace);
    if (!composedDsl?.trim()) {
      return null;
    }

    // Append .output() terminal
    const execDsl = appendOutputTerminal(composedDsl, step);
    if (!execDsl) {
      return null;
    }

    set({ executingStepId: stepId, executionJobId: null });

    const { data, error } = await api.api.v1['visual-pipelines']({ id: pipelineId })['execute-step'].post({
      dsl: execDsl,
      step_name: step.name || undefined,
    });
    if (error || !data || !('job_id' in data)) {
      console.error('Failed to execute step:', error);
      set({ executingStepId: null, executionJobId: null });
      return null;
    }
    set({ executingStepId: null, executionJobId: data.job_id });
    return { jobId: data.job_id, jobName: data.job_name };
  },

  // ── Chart actions ──
  addChart: () => {
    const id = generateId();
    set((state) => ({
      charts: [
        ...state.charts,
        {
          id,
          name: '',
          dependsOn: [],
          dsl: '',
        },
      ],
      selectedStepId: `chart_${id}`,
      isDirty: true,
    }));
    return id;
  },

  updateChart: (id: string, partial: Partial<PipelineChart>) =>
    set((state) => ({
      charts: state.charts.map((c) => (c.id === id ? { ...c, ...partial } : c)),
      isDirty: true,
    })),

  removeChart: (id: string) =>
    set((state) => ({
      charts: state.charts.filter((c) => c.id !== id),
      selectedStepId: state.selectedStepId === `chart_${id}` ? null : state.selectedStepId,
      isDirty: true,
    })),

  previewChart: (id: string) => set({ chartPreviewId: id, showChartPreview: true }),

  closeChartPreview: () => set({ chartPreviewId: null, showChartPreview: false }),

  // ── Alert actions ──
  addAlert: () => {
    const id = generateId();
    set((state) => ({
      alerts: [
        ...state.alerts,
        {
          id,
          name: '',
          title: '',
          description: '',
          severity: 'warning' as const,
          dsl: '',
          channels: [],
          dependsOn: [],
          order: state.alerts.length,
          trigger: null,
        },
      ],
      selectedStepId: `alert_${id}`,
      isDirty: true,
    }));
    return id;
  },

  updateAlert: (id: string, partial: Partial<AlertStep>) =>
    set((state) => ({
      alerts: state.alerts.map((a) => (a.id === id ? { ...a, ...partial } : a)),
      isDirty: true,
    })),

  removeAlert: (id: string) =>
    set((state) => ({
      alerts: state.alerts.filter((a) => a.id !== id),
      selectedStepId: state.selectedStepId === `alert_${id}` ? null : state.selectedStepId,
      isDirty: true,
    })),

  // ── Agent steps ──
  addAgentStep: () => {
    const id = generateId();
    set((state) => ({
      agentSteps: [
        ...state.agentSteps,
        {
          id,
          name: '',
          repo: '',
          task: '',
          phase: 'plan' as const,
          baseBranch: 'main',
          dataSource: 'solidafy-agent',
          dependsOn: [],
          order: state.agentSteps.length,
        },
      ],
      selectedStepId: `agent_${id}`,
      isDirty: true,
    }));
    return id;
  },

  updateAgentStep: (id: string, partial: Partial<AgentStep>) =>
    set((state) => ({
      agentSteps: state.agentSteps.map((a) => (a.id === id ? { ...a, ...partial } : a)),
      isDirty: true,
    })),

  removeAgentStep: (id: string) =>
    set((state) => ({
      agentSteps: state.agentSteps.filter((a) => a.id !== id),
      selectedStepId: state.selectedStepId === `agent_${id}` ? null : state.selectedStepId,
      isDirty: true,
    })),

  getChartComposedDsl: (chartId: string) => {
    const chart = get().charts.find((c) => c.id === chartId);
    if (!chart) {
      return '';
    }
    const { trigger, dataSources, transformSteps } = get();
    const ds = trigger.dataSourceId ? dataSources.find((d) => d.id === trigger.dataSourceId) : null;
    return composeChartPreviewDsl(chart, transformSteps, ds?.namespace);
  },

  // ── Push charts to dashboard ──
  pushChartsToDashboard: async () => {
    const s = get();
    if (s.charts.length === 0) {
      return null;
    }

    set({ isPushingDashboard: true });
    try {
      // Resolve namespace for ${NAMESPACE} substitution
      const ds = s.trigger.dataSourceId ? s.dataSources.find((d) => d.id === s.trigger.dataSourceId) : null;
      const ns = ds?.namespace || '';
      const resolveDsl = (dsl: string | undefined) =>
        (dsl || '').replace(/\$\{NAMESPACE\}/g, ns).replace(/@\{NAMESPACE\}/g, ns);
      // Drilldown can be a single string (legacy) or a chain (array of
      // strings). Resolve the namespace in each entry independently;
      // undefined / empty values pass through as undefined so the chart
      // renders as non-drillable.
      const resolveDrilldown = (dsl: string | string[] | undefined): string | string[] | undefined => {
        if (!dsl) {
          return undefined;
        }
        if (Array.isArray(dsl)) {
          const chain = dsl.filter((d): d is string => typeof d === 'string' && d.length > 0).map(resolveDsl);
          return chain.length === 0 ? undefined : chain;
        }
        return resolveDsl(dsl);
      };

      // Separate KPI charts from regular charts for layout
      const kpiCharts: PipelineChart[] = [];
      const regularCharts: PipelineChart[] = [];
      for (const chart of s.charts) {
        const typeMatch = chart.dsl?.match(/\.chart\(\s*(\w+)/i);
        if (typeMatch && typeMatch[1].toLowerCase() === 'kpi') {
          kpiCharts.push(chart);
        } else {
          regularCharts.push(chart);
        }
      }

      const chartDefs: ChartDefinition[] = [];

      // KPIs: use stored grid if present, otherwise 3 wide, 2 tall, 4 across per row
      for (let i = 0; i < kpiCharts.length; i++) {
        const kpi = kpiCharts[i];
        chartDefs.push({
          id: kpi.id,
          title: humanizeTitle(kpi.name || `KPI ${i + 1}`),
          subtitle: kpi.subtitle || undefined,
          description: kpi.description || undefined,
          dsl: resolveDsl(kpi.dsl),
          drilldown_dsl: resolveDrilldown(kpi.drilldown_dsl),
          tab: kpi.tab || undefined,
          grid: kpi.grid || { x: (i % 4) * 3, y: Math.floor(i / 4) * 2, w: 3, h: 2 },
        });
      }

      // Regular charts: use stored grid if present, otherwise 6 wide, 4 tall, 2 per row below KPIs
      const yOffset = kpiCharts.length > 0 ? Math.ceil(kpiCharts.length / 4) * 2 : 0;
      for (let i = 0; i < regularCharts.length; i++) {
        const rc = regularCharts[i];
        chartDefs.push({
          id: rc.id,
          title: humanizeTitle(rc.name || `Chart ${i + 1}`),
          subtitle: rc.subtitle || undefined,
          description: rc.description || undefined,
          dsl: resolveDsl(rc.dsl),
          drilldown_dsl: resolveDrilldown(rc.drilldown_dsl),
          tab: rc.tab || undefined,
          grid: rc.grid || { x: (i % 2) * 6, y: yOffset + Math.floor(i / 2) * 4, w: 6, h: 4 },
        });
      }

      // SOL-82: Convert pipeline filters → dashboard FilterDefinition[].
      // Global filters get static options (e.g. time-range picklist).
      // Auto filters get column/operator + options_dsl for dynamic option loading.
      const filterDefs: FilterDefinition[] = s.filters.map((f) => {
        const def: FilterDefinition = {
          id: crypto.randomUUID(),
          label: f.label,
          type: (f.type || 'select') as FilterType,
          scope: f.scope,
          default: f.default,
        };
        if (f.scope === 'global') {
          def.options = (f.options || []).map((o) => ({ value: o.value, label: o.label }));
        }
        if (f.scope === 'auto') {
          def.column = f.column;
          def.operator = f.operator || '==';
          if (f.options_dsl) {
            def.options_dsl = resolveDsl(f.options_dsl);
          }
        }
        return def;
      });

      const spec: DashboardSpec = {
        dashboard: { id: s.pipelineId || 'pipeline', title: humanizeTitle(s.pipelineName) },
        filters: filterDefs.length > 0 ? filterDefs : undefined,
        charts: chartDefs,
      };

      let dashId = s.dashboardId;
      if (dashId) {
        try {
          await updateDashboard(dashId, { name: s.pipelineName, spec });
        } catch {
          // Dashboard was deleted — create a new one
          dashId = null;
        }
      }
      if (!dashId) {
        const created = await createDashboard({ name: s.pipelineName, spec });
        dashId = created.id;
        set({ dashboardId: dashId, isDirty: true });
        await get().savePipeline();
      }

      set({ isPushingDashboard: false });
      return dashId;
    } catch (err) {
      console.error('Failed to push charts to dashboard:', err);
      set({ isPushingDashboard: false });
      return null;
    }
  },

  // ── Apply template ──
  applyTemplate: async (templateName: string) => {
    try {
      // Fetch full template from registry (includes parsed YAML with DSL)
      const { data: tmpl, error: tmplError } = await api.api.v1.registry.pipelines({ name: templateName }).get();
      if (tmplError || !tmpl) {
        throw new Error('Failed to load template');
      }

      // Build ingest steps from v2 connector steps in YAML
      const ingestSteps: IngestStep[] = [];
      if (tmpl.yaml) {
        try {
          const parsed = yaml.load(tmpl.yaml) as { steps?: Record<string, unknown>[] } | null;
          if (parsed && Array.isArray(parsed.steps)) {
            for (const step of parsed.steps) {
              if (step.type !== 'connector') {
                continue;
              }
              const dsl = (step.dsl as string) || '';
              const connectorMatch = dsl.match(/connector\("([^"]+)"/);
              const streamMatch = dsl.match(/\.stream\("([^"]+)"/);
              ingestSteps.push({
                id: generateId(),
                name: (step.name as string) || '',
                dsl,
                connectorType: connectorMatch?.[1] || '',
                streamName: streamMatch?.[1] || '',
                order: ingestSteps.length,
                dependsOn: Array.isArray(step.depends_on) ? (step.depends_on as string[]) : [],
              });
            }
          }
        } catch {
          // Ignore parse errors
        }
      }

      // Set pipeline name and track the template origin
      set({
        pipelineName: humanizeTitle(tmpl.title || tmpl.name || templateName),
        templateName,
        ingestSteps,
      });

      // Build template stream map — streams can be string[], object[], or Record
      const templateStreams: Record<string, string | null> = {};
      if (tmpl.streams) {
        if (Array.isArray(tmpl.streams)) {
          for (const s of tmpl.streams) {
            if (typeof s === 'string') {
              templateStreams[s] = null;
            } else if (s && typeof s === 'object' && 'name' in s) {
              const obj = s as { name: string; cursor_field?: string };
              templateStreams[obj.name] = obj.cursor_field || null;
            }
          }
        } else if (typeof tmpl.streams === 'object') {
          for (const [name, cursor] of Object.entries(tmpl.streams as Record<string, unknown>)) {
            templateStreams[name] = typeof cursor === 'string' ? cursor : null;
          }
        }
      }

      // Try to match a data source by connector type
      if (get().dataSources.length === 0) {
        await get().loadDataSources();
      }
      const connector = tmpl.connector;
      if (connector) {
        // First set template streams on trigger so setTrigger merges them
        if (Object.keys(templateStreams).length > 0) {
          set((state) => ({
            trigger: { ...state.trigger, streams: templateStreams },
          }));
        }
        const matchingDs = get().dataSources.find((ds) => ds.connector_type === connector);
        if (matchingDs) {
          get().setTrigger(matchingDs.id);
        } else {
          set({
            trigger: {
              type: 'datasource',
              connectorType: connector,
              dataSourceName: connector,
              streams: templateStreams,
            },
          });
        }
      }

      // Build transform steps from materialize_configs (full objects with DSL)
      const mats = tmpl.materialize_configs || [];
      if (mats.length > 0) {
        const steps: TransformStep[] = mats.map((mat, idx) => {
          const dependsOn = mat.depends_on || [];
          const conflictKeys = mat.conflict_keys || [];
          const partitionBy = mat.partition_by || [];

          // Split "silver.transactions" → namespace + table
          const rawOutput = mat.output || '';
          const dotIdx = rawOutput.indexOf('.');
          const outputNamespace = dotIdx > 0 ? rawOutput.slice(0, dotIdx) : 'silver';
          const outputTable = dotIdx > 0 ? rawOutput.slice(dotIdx + 1) : rawOutput;

          return {
            id: generateId(),
            name: mat.name || `Step ${idx + 1}`,
            transformNodes: [],
            transformEdges: [],
            dsl: mat.dsl || mat.sql || '',
            outputNamespace,
            outputTable,
            mode: (mat.mode || 'replace') as TransformStep['mode'],
            conflictKeys,
            partitionBy,
            dependsOn,
            order: idx,
          };
        });
        set({ transformSteps: steps, isDirty: true });
      }

      // Extract charts from template
      const tmplCharts = tmpl.charts || [];
      if (tmplCharts.length > 0) {
        const charts: PipelineChart[] = tmplCharts.map((ch) => ({
          id: generateId(),
          name: ch.name || 'Untitled Chart',
          subtitle: ch.subtitle || undefined,
          description: ch.description || undefined,
          dependsOn: ch.depends_on || [],
          dsl: ch.dsl || '',
          drilldown_dsl: ch.drilldown_dsl || undefined,
          tab: ch.tab || undefined,
          grid: ch.grid || undefined,
        }));
        set({ charts });
      }

      // Extract alerts from template
      const tmplAlerts = tmpl.alerts || [];
      if (tmplAlerts.length > 0) {
        const alerts: AlertStep[] = tmplAlerts.map((al, idx) => {
          const rawTrigger = (al as Record<string, unknown>).trigger as string | undefined;
          return {
            id: generateId(),
            name: al.name || '',
            title: al.title || '',
            description: al.description || '',
            severity: (al.severity || 'warning') as AlertStep['severity'],
            dsl: al.dsl || '',
            channels: al.channels || [],
            dependsOn: al.depends_on || [],
            order: idx,
            trigger:
              rawTrigger === 'on_success' || rawTrigger === 'on_failure' || rawTrigger === 'on_complete'
                ? rawTrigger
                : null,
          };
        });
        set({ alerts });
      }

      // Extract filters from template
      const tmplFilters = tmpl.filters;
      if (Array.isArray(tmplFilters) && tmplFilters.length > 0) {
        set({ filters: tmplFilters as PipelineFilter[] });
      }

      // Extract seed tables from template response — always set (clear if template has none)
      const tmplSeeds = (tmpl as { seed?: Record<string, unknown>[] }).seed;
      if (Array.isArray(tmplSeeds) && tmplSeeds.length > 0) {
        const seedTables: SeedTableDef[] = tmplSeeds
          .map((s) => ({
            name: (s.name as string) || '',
            output: (s.output as string) || '',
            mode: (s.mode as 'merge' | 'replace') || 'merge',
            primary_key: (s.primary_key as string[]) || [],
            columns: Array.isArray(s.columns)
              ? (s.columns as Record<string, unknown>[]).map((c) => ({
                  name: (c.name as string) || '',
                  type: c.type as string | undefined,
                  seeded: c.seeded as boolean | undefined,
                  editable: c.editable as boolean | undefined,
                  lookup: c.lookup as SeedColumnDef['lookup'],
                  description: c.description as string | undefined,
                }))
              : [],
            dsl: s.dsl as string | undefined,
            default_rows: s.default_rows as Record<string, unknown>[] | undefined,
            depends_on: s.depends_on as string[] | undefined,
            description: s.description as string | undefined,
          }))
          .filter((t) => t.name);
        set({ seedTables });
      } else {
        set({ seedTables: [] });
      }
      set({ isDirty: true });
    } catch (err) {
      console.error('Failed to apply template:', err);
    }
  },

  // ── Refresh template (re-apply + save + update dashboard) ──
  refreshTemplate: async () => {
    const s = get();
    if (!s.templateName) {
      return false;
    }
    try {
      await get().applyTemplate(s.templateName);
      const saved = await get().savePipeline();
      if (!saved) {
        return false;
      }
      // If there are charts, push/update the dashboard too
      if (get().charts.length > 0) {
        await get().pushChartsToDashboard();
      }
      return true;
    } catch (err) {
      console.error('Failed to refresh template:', err);
      return false;
    }
  },

  // ── Import from AI-generated YAML ──
  importFromYaml: async (yamlStr: string) => {
    const parsed = yaml.load(yamlStr) as {
      name?: string;
      description?: string;
      materialize?: Array<{
        name?: string;
        dsl?: string;
        output?: string;
        mode?: string;
        depends_on?: string[];
      }>;
    };

    const mats = parsed.materialize || [];
    const steps: TransformStep[] = [];
    const charts: PipelineChart[] = [];

    for (let i = 0; i < mats.length; i++) {
      const mat = mats[i];
      const dsl = mat.dsl || '';
      const dependsOn = mat.depends_on || [];

      if (dsl.includes('.chart(')) {
        // Chart materialization
        charts.push({
          id: generateId(),
          name: mat.name || `Chart ${charts.length + 1}`,
          dependsOn,
          dsl,
        });
      } else {
        // Transform step
        const rawOutput = mat.output || '';
        const dotIdx = rawOutput.indexOf('.');
        const outputNamespace = dotIdx > 0 ? rawOutput.slice(0, dotIdx) : 'silver';
        const outputTable = dotIdx > 0 ? rawOutput.slice(dotIdx + 1) : rawOutput;

        steps.push({
          id: generateId(),
          name: mat.name || `Step ${steps.length + 1}`,
          transformNodes: [],
          transformEdges: [],
          dsl,
          outputNamespace,
          outputTable,
          mode: (mat.mode || 'replace') as TransformStep['mode'],
          conflictKeys: [],
          partitionBy: [],
          dependsOn,
          order: steps.length,
        });
      }
    }

    // Reset builder state and populate with parsed data
    get().resetBuilder();
    set({
      pipelineName: parsed.name || 'Scout Pipeline',
      pipelineDescription: parsed.description || '',
      trigger: { type: 'none' },
      transformSteps: steps,
      charts,
      alerts: [],
      filters: [],
      isDirty: true,
    });

    // Save the pipeline
    const saved = await get().savePipeline();
    if (!saved) {
      throw new Error('Failed to save pipeline');
    }
    const pipelineId = get().pipelineId!;

    // Push charts to dashboard if any exist
    let dashboardId: string | null = null;
    if (charts.length > 0) {
      dashboardId = await get().pushChartsToDashboard();
    }

    return { pipelineId, dashboardId };
  },

  // ── Import from a single DSL query (AI chat) ──
  importFromDsl: async (dsl: string, name?: string) => {
    const hasChart = dsl.includes('.chart(');
    const steps: TransformStep[] = [];
    const charts: PipelineChart[] = [];

    if (hasChart) {
      const titleMatch = dsl.match(/title:\s*"([^"]+)"/);
      charts.push({
        id: generateId(),
        name: titleMatch?.[1] || name || 'Scout Chart',
        dependsOn: [],
        dsl,
      });

      // Also add the DSL without .chart() as a transform step
      const baseDsl = dsl.replace(/\s*\.chart\([^)]*\)\s*$/, '').trim();
      if (baseDsl) {
        const sourceMatch = baseDsl.match(/source\("([^"]+)"\)/);
        const sourceParts = sourceMatch?.[1]?.split('.') || [];
        steps.push({
          id: generateId(),
          name: name || 'Scout Transform',
          transformNodes: [],
          transformEdges: [],
          dsl: baseDsl,
          outputNamespace: 'silver',
          outputTable: sourceParts[sourceParts.length - 1] || 'scout_output',
          mode: 'replace',
          conflictKeys: [],
          partitionBy: [],
          dependsOn: [],
          order: 0,
        });
      }
    } else {
      const sourceMatch = dsl.match(/source\("([^"]+)"\)/);
      const sourceParts = sourceMatch?.[1]?.split('.') || [];
      steps.push({
        id: generateId(),
        name: name || 'Scout Transform',
        transformNodes: [],
        transformEdges: [],
        dsl,
        outputNamespace: 'silver',
        outputTable: sourceParts[sourceParts.length - 1] || 'scout_output',
        mode: 'replace',
        conflictKeys: [],
        partitionBy: [],
        dependsOn: [],
        order: 0,
      });
    }

    get().resetBuilder();
    set({
      pipelineName: name || 'Scout Pipeline',
      pipelineDescription: '',
      trigger: { type: 'none' },
      transformSteps: steps,
      charts,
      alerts: [],
      filters: [],
      isDirty: true,
    });

    const saved = await get().savePipeline();
    if (!saved) {
      throw new Error('Failed to save pipeline');
    }
    const pipelineId = get().pipelineId!;

    let dashboardId: string | null = null;
    if (charts.length > 0) {
      dashboardId = await get().pushChartsToDashboard();
    }

    return { pipelineId, dashboardId };
  },

  // ── Reset ──
  resetBuilder: () =>
    set({
      pipelineId: null,
      pipelineName: 'Untitled Pipeline',
      pipelineDescription: '',
      pipelineStatus: 'draft',
      pipelineSchedule: null,
      templateName: null,
      trigger: { ...defaultTrigger },
      ingestSteps: [],
      transformSteps: [],
      charts: [],
      alerts: [],
      filters: [],
      selectedStepId: null,
      drilledStepId: null,
      isDirty: false,
      isSaving: false,
      isRunning: false,
      runError: null,
      isFullyLoaded: true,
      runs: [],
      previewStepId: null,
      previewLoading: false,
      previewData: null,
      showPreviewModal: false,
      chartPreviewId: null,
      showChartPreview: false,
      executingStepId: null,
      executionJobId: null,
      dashboardId: null,
      isPushingDashboard: false,
    }),
}));
